import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import {
  Sparkles, Users, Search, Mail, FileUp, Send, Loader2, Plus, Trash2,
  CheckCircle2, AlertTriangle, RefreshCw, ChevronLeft, ChevronRight, X, ShieldCheck, UserPlus,
  Eye, Code2, ShieldAlert, Wand2, Ban,
} from "lucide-react";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { API, TIERS, SECTORS } from "@/lib/constants";
import P1GreetingPanel from "@/components/P1GreetingPanel";
import { SchedulePanel } from "@/components/SchedulePanel";
import { SequencePanel } from "@/components/SequencePanel";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const TARGET_TYPES = [
  { value: "hr", label: "HR leads" },
  { value: "finance", label: "Finance" },
  { value: "executive", label: "Executive / C-suite" },
  { value: "operations", label: "Operations" },
  { value: "legal", label: "Legal" },
  { value: "sales", label: "Sales" },
  { value: "it", label: "IT" },
  { value: "marketing", label: "Marketing" },
];

const SENIORITY = [
  { value: "executive,senior", label: "Executive + Senior" },
  { value: "executive", label: "Executive only" },
  { value: "executive,senior,junior", label: "All seniorities" },
];

const STEPS = ["Setup", "Contacts", "Compose", "Files", "Review & Send"];

const verifyBadge = (status) => {
  const map = {
    valid: ["bg-emerald-100 text-emerald-700 border-emerald-200", "Verified"],
    accept_all: ["bg-amber-100 text-amber-700 border-amber-200", "Catch-all"],
    webmail: ["bg-blue-100 text-blue-700 border-blue-200", "Personal"],
    unknown: ["bg-slate-100 text-slate-600 border-slate-200", "Unconfirmed"],
    invalid: ["bg-rose-100 text-rose-700 border-rose-200", "Invalid"],
    disposable: ["bg-rose-100 text-rose-700 border-rose-200", "Disposable"],
  };
  const [cls, label] = map[status] || map.unknown;
  return <Badge variant="outline" className={cls}>{label}</Badge>;
};

const isHardBlocked = (s) => s === "invalid" || s === "disposable";

// Strip obviously dangerous bits from pasted signature HTML (it's admin-entered
// and goes into emails, but we still remove scripts and inline event handlers).
const sanitizeSignature = (html) => (html || "")
  .replace(/<script[\s\S]*?<\/script>/gi, "")
  .replace(/<style[\s\S]*?<\/style>/gi, "")
  .replace(/ on\w+="[^"]*"/gi, "")
  .replace(/ on\w+='[^']*'/gi, "");

// Rich, paste-friendly signature box. contentEditable preserves the formatting
// (logo, links, fonts) when you paste a signature straight from Outlook/Gmail.
function SignatureEditor({ value, onChange, testid }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.innerHTML = value || "";
    // initialise once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div
      ref={ref}
      contentEditable
      suppressContentEditableWarning
      data-testid={testid}
      onInput={() => onChange(ref.current.innerHTML)}
      onBlur={() => onChange(ref.current.innerHTML)}
      className="min-h-[90px] max-h-[220px] overflow-auto border rounded-md bg-white p-2 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-400"
    />
  );
}

export default function BespokeCampaignWizard({ resumeId, onResumed }) {
  const [step, setStep] = useState(0);
  const [maxStep, setMaxStep] = useState(0);
  const [busy, setBusy] = useState(false);

  // Setup
  const [clients, setClients] = useState([]);
  const [senders, setSenders] = useState([]);
  const [name, setName] = useState("");
  const [targetType, setTargetType] = useState("hr");
  const [seniority, setSeniority] = useState("executive,senior");
  const [senderId, setSenderId] = useState("");
  const [brief, setBrief] = useState("");
  const [cta, setCta] = useState("");
  const [sendMode, setSendMode] = useState("preserve"); // preserve (default) | compose
  const [masterTemplate, setMasterTemplate] = useState("");
  const [masterSubject, setMasterSubject] = useState("");
  const [selectedClients, setSelectedClients] = useState([]);
  const [filterSector, setFilterSector] = useState("all");
  const [filterTier, setFilterTier] = useState("all");

  // Campaign state
  const [campaign, setCampaign] = useState(null);
  const [recipients, setRecipients] = useState([]); // grouped
  const [testMode, setTestMode] = useState(true);
  const [code, setCode] = useState("");
  const [sendResult, setSendResult] = useState(null);

  // Manual add form
  const [manualFor, setManualFor] = useState(null);
  const [manual, setManual] = useState({ name: "", email: "", title: "" });

  // Pre-flight (selection-time, local, no Hunter) + deliberate discovery
  const [preflight, setPreflight] = useState(null);
  const [candidates, setCandidates] = useState({});   // client_id -> [candidate]
  const [discoveringFor, setDiscoveringFor] = useState(null);
  const [lateClientId, setLateClientId] = useState("");  // client picker for add-at-any-stage
  const [ackDialog, setAckDialog] = useState(null);       // {clientId, verify_status, hard_blocked, message}

  // Add-sender form (sender management moved here from Mission Control)
  const [showAddSender, setShowAddSender] = useState(false);
  const [newSender, setNewSender] = useState({ display_name: "", email: "", title: "", signature_html: "" });
  // Edit signature for an existing selected sender
  const [editSig, setEditSig] = useState(false);
  const [sigDraft, setSigDraft] = useState("");
  // Per-recipient "show rendered preview" toggles (Compose step)
  const [previews, setPreviews] = useState({});
  const togglePreview = (rid) => setPreviews((p) => ({ ...p, [rid]: !p[rid] }));

  const api = useCallback(async (path, opts = {}) => {
    const res = await fetch(`${API}${path}`, { credentials: "include", ...opts });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.detail || "Request failed");
    return data;
  }, []);

  const loadSenders = useCallback(async (selectNewest = false) => {
    const s = await fetch(`${API}/mission-control/senders`, { credentials: "include" }).then((r) => r.json());
    const active = (s.senders || []).filter((x) => x.is_active);
    setSenders(active);
    if (selectNewest && active[0]) setSenderId(active[0].sender_id);
    else if (!selectNewest && active[0] && !senderId) setSenderId(active[0].sender_id);
    return active;
  }, [senderId]);

  useEffect(() => {
    (async () => {
      try {
        const c = await fetch(`${API}/clients`, { credentials: "include" }).then((r) => r.json());
        setClients(Array.isArray(c) ? c : []);
        await loadSenders();
      } catch (e) { /* noop */ }
    })();
  }, []); // eslint-disable-line

  // Keep the furthest-reached step so the stepper can offer back-navigation.
  useEffect(() => { setMaxStep((m) => Math.max(m, step)); }, [step]);

  // Which wizard step a saved campaign should reopen on, based on its status.
  const stepForStatus = (status) => {
    switch (status) {
      case "drafting": return 2;
      case "pending_sender_verification":
      case "scheduled": return 4;
      case "draft":
      case "contacts_review":
      default: return 1;
    }
  };

  // Reopen a saved (unsent) campaign in the wizard at the step it was left on.
  const loadExisting = useCallback(async (id) => {
    setBusy(true);
    try {
      const d = await api(`/campaigns/bespoke/${id}`);
      const c = d.campaign;
      setCampaign(c);
      setRecipients(d.recipients || []);
      setTestMode(!!c.test_mode);
      setName(c.name || "");
      setTargetType(c.target_type || "hr");
      setSeniority(c.seniority || "executive,senior");
      setSenderId(c.sender_id || "");
      setBrief(c.campaign_brief || "");
      setCta(c.default_cta || "");
      setSendMode(c.send_mode || "preserve");
      setMasterTemplate(c.master_template || "");
      setMasterSubject(c.master_subject || "");
      setSelectedClients(c.client_ids || []);
      const s = stepForStatus(c.status);
      setMaxStep(s);
      setStep(s);
      toast.success(`Resumed "${c.name}" — pick up where you left off`);
    } catch (e) {
      toast.error(e.message || "Couldn't load that campaign");
    } finally {
      setBusy(false);
      onResumed && onResumed();
    }
  }, [api, onResumed]);

  useEffect(() => {
    if (resumeId) loadExisting(resumeId);
  }, [resumeId]); // eslint-disable-line

  // Clear the wizard back to a blank new-campaign state (used after resuming
  // one campaign and wanting to start a fresh one without a page reload).
  const resetWizard = () => {
    setCampaign(null);
    setRecipients([]);
    setStep(0);
    setMaxStep(0);
    setName("");
    setTargetType("hr");
    setSeniority("executive,senior");
    setBrief("");
    setCta("");
    setSendMode("preserve");
    setMasterTemplate("");
    setMasterSubject("");
    setSelectedClients([]);
    setSendResult(null);
    setCode("");
    setTestMode(true);
    if (senders[0]) setSenderId(senders[0].sender_id);
  };

  const addSender = async () => {
    if (!newSender.email.includes("@") || !newSender.display_name.trim()) {
      return toast.error("Name and a valid email are required");
    }
    setBusy(true);
    try {
      const created = await api(`/mission-control/senders`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...newSender, signature_html: sanitizeSignature(newSender.signature_html) }),
      });
      const active = await loadSenders();
      // select the just-added one
      const match = active.find((s) => s.email.toLowerCase() === newSender.email.toLowerCase());
      if (match) setSenderId(match.sender_id);
      else if (created?.sender_id) setSenderId(created.sender_id);
      setNewSender({ display_name: "", email: "", title: "", signature_html: "" });
      setShowAddSender(false);
      toast.success("Sender added");
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const saveSignature = async () => {    setBusy(true);
    try {
      await api(`/mission-control/senders/${senderId}`, {
        method: "PATCH", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ signature_html: sanitizeSignature(sigDraft) }),
      });
      await loadSenders();
      setEditSig(false);
      toast.success("Signature saved");
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const filteredClients = clients.filter((c) =>
    (filterSector === "all" || (c.sector || "").includes(filterSector)) &&
    (filterTier === "all" || c.tier === filterTier)
  );

  // Auto-tag: REX inserts [Name]/[Company]/[Signal] into the pasted email without
  // rewriting any words. Operator reviews the result in the textarea.
  const autoTagTemplate = async () => {
    if (!masterTemplate.trim()) return toast.error("Paste your email first");
    setBusy(true);
    try {
      const d = await api(`/campaigns/bespoke/auto-tag`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: masterTemplate }),
      });
      setMasterTemplate(d.tagged);
      if (d.warnings && d.warnings.length) toast.warning(d.warnings[0]);
      else if (d.changed) toast.success("Rex tagged [Name], [Company] and [Signal] — review below");
      else toast.message("Looks already tagged — no changes needed");
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const refreshCampaign = useCallback(async (id) => {
    const d = await api(`/campaigns/bespoke/${id}`);
    setCampaign(d.campaign);
    setRecipients(d.recipients || []);
    setTestMode(!!d.campaign.test_mode);
    return d;
  }, [api]);

  // ── Step 1 → create + resolve ────────────────────────────────
  const createAndResolve = async () => {
    if (!name.trim()) return toast.error("Give the campaign a name");
    if (!senderId) return toast.error("Select a sender");
    if (sendMode === "preserve" && !masterTemplate.trim())
      return toast.error("Paste your approved master template (Preserve mode)");
    if (sendMode === "preserve" && !masterSubject.trim())
      return toast.error("Add a subject line (Preserve mode)");
    if (sendMode === "compose" && !brief.trim())
      return toast.error("Write a campaign brief (Compose mode)");
    if (selectedClients.length === 0) return toast.error("Select at least one client");
    setBusy(true);
    try {
      const created = await api(`/campaigns/bespoke`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name, target_type: targetType, seniority, sender_id: senderId,
          campaign_brief: brief, default_cta: cta, client_ids: selectedClients,
          send_mode: sendMode, master_template: masterTemplate, master_subject: masterSubject,
          test_mode: true,
        }),
      });
      const id = created.campaign.campaign_id;
      setCampaign(created.campaign);
      toast.message("Finding contacts via Hunter.io…");
      await api(`/campaigns/bespoke/${id}/resolve-contacts`, { method: "POST" });
      await refreshCampaign(id);
      setStep(1);
      toast.success("Contacts resolved");
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  // Validate the Setup fields shared by create / save / re-resolve.
  const validateSetup = ({ requireClients = true } = {}) => {
    if (!name.trim()) { toast.error("Give the campaign a name"); return false; }
    if (!senderId) { toast.error("Select a sender"); return false; }
    if (sendMode === "preserve" && !masterTemplate.trim()) { toast.error("Paste your approved master template (Preserve mode)"); return false; }
    if (sendMode === "preserve" && !masterSubject.trim()) { toast.error("Add a subject line (Preserve mode)"); return false; }
    if (sendMode === "compose" && !brief.trim()) { toast.error("Write a campaign brief (Compose mode)"); return false; }
    if (requireClients && selectedClients.length === 0) { toast.error("Select at least one client"); return false; }
    return true;
  };

  // Persist Setup edits to an EXISTING campaign (no destructive re-resolve).
  const patchSetup = async () => api(`/campaigns/bespoke/${campaign.campaign_id}`, {
    method: "PATCH", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      name, target_type: targetType, seniority, sender_id: senderId,
      client_ids: selectedClients, campaign_brief: brief, default_cta: cta,
      send_mode: sendMode, master_template: masterTemplate, master_subject: masterSubject,
    }),
  });

  // Edit-mode: save Setup changes and continue to Contacts WITHOUT re-running Hunter.
  const saveSetup = async () => {
    if (!validateSetup({ requireClients: false })) return;
    setBusy(true);
    try {
      await patchSetup();
      await refreshCampaign(campaign.campaign_id);
      setStep(1);
      toast.success("Setup saved");
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  // Edit-mode: re-run Hunter for the (possibly changed) audience. This rebuilds
  // the contact list, so warn the operator that contact-level edits are replaced.
  const reResolve = async () => {
    if (!validateSetup({ requireClients: true })) return;
    if (!window.confirm("Re-find contacts via Hunter.io? This rebuilds the contact list for the selected audience and may replace contacts (and their drafts) from a previous search.")) return;
    setBusy(true);
    try {
      await patchSetup();
      toast.message("Finding contacts via Hunter.io…");
      await api(`/campaigns/bespoke/${campaign.campaign_id}/resolve-contacts`, { method: "POST" });
      await refreshCampaign(campaign.campaign_id);
      setStep(1);
      toast.success("Contacts re-found");
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const patchRecipient = async (rid, body) => {
    await api(`/campaigns/bespoke/${campaign.campaign_id}/recipients/${rid}`, {
      method: "PATCH", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    await refreshCampaign(campaign.campaign_id);
  };

  const autoFixStyle = async (rid) => {
    setBusy(true);
    try {
      const d = await api(`/campaigns/bespoke/${campaign.campaign_id}/recipients/${rid}/auto-fix-style`, { method: "POST" });
      if (d.recipients) setRecipients(d.recipients);
      const n = (d.remaining_flags || []).length;
      toast.success(n ? `Mechanical issues fixed · ${n} left for manual review` : "House-style fixed — all clear");
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const finalizeAdd = async (d) => {
    setManual({ name: "", email: "", title: "" });
    setManualFor(null);
    await refreshCampaign(campaign.campaign_id);
    const bits = [];
    if (d.written_to_client) bits.push("saved to the client record");
    if (d.drafted) bits.push("drafted for review");
    if (d.recipient && !d.drafted) {
      toast.warning("Contact added and saved, but not drafted yet — run 'Rex draft per client' so it has something to send.");
    } else {
      toast.success(`Contact added${bits.length ? " · " + bits.join(" · ") : ""}`);
    }
  };

  const submitAdd = async (clientId, ack) => {
    setBusy(true);
    try {
      const d = await api(`/campaigns/bespoke/${campaign.campaign_id}/recipients`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ client_id: clientId, ...manual, acknowledge_unverified: ack }),
      });
      if (d && d.needs_acknowledgement) {
        // Non-blocking verify: open the app's dialog carrying how risky it is (hard bounce vs uncertain).
        setAckDialog({ clientId, ...d });
        return;
      }
      await finalizeAdd(d);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const addManual = (clientId) => {
    if (!manual.email.includes("@")) return toast.error("Enter a valid email");
    return submitAdd(clientId, false);
  };

  const proceedAck = async () => {
    const clientId = ackDialog?.clientId;
    setAckDialog(null);
    if (clientId) await submitAdd(clientId, true);
  };

  // ── Pre-flight at selection (local only; no Hunter) ──
  useEffect(() => {
    if (step !== 0 || selectedClients.length === 0 || !targetType) { setPreflight(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const d = await api(`/campaigns/bespoke/preflight`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ client_ids: selectedClients, target_type: targetType }),
        });
        if (!cancelled) setPreflight(d);
      } catch { if (!cancelled) setPreflight(null); }
    })();
    return () => { cancelled = true; };
  }, [step, selectedClients, targetType, api]);

  // ── Deliberate discovery for a domain-searchable client (the action) ──
  const findContacts = async (clientId) => {
    setDiscoveringFor(clientId);
    try {
      const d = await api(`/campaigns/bespoke/discover-candidates`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ client_id: clientId, target_type: targetType }),
      });
      setCandidates((p) => ({ ...p, [clientId]: d.candidates || [] }));
      if (!(d.candidates || []).length) toast.info("Hunter found no contacts for that domain");
    } catch (e) { toast.error(e.message); } finally { setDiscoveringFor(null); }
  };

  const confirmCandidate = async (clientId, cand) => {
    setBusy(true);
    try {
      const d = await api(`/campaigns/bespoke/confirm-candidate`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ client_id: clientId, name: cand.name, email: cand.email, title: cand.title }),
      });
      setCandidates((p) => ({ ...p, [clientId]: (p[clientId] || []).filter((x) => x.email !== cand.email) }));
      toast.success(d.written_to_client ? `Saved to client · verify: ${d.verify_status}` : "Already on the client record");
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  // Add a contact at ANY later stage (compose / review), including after drafts exist.
  // Reuses addManual — the backend drafts the new one and writes it back to the client.
  const renderLateAdd = () => {
    const addable = (recipients || []).filter((g) => !g.restricted);
    return (
      <div className="border rounded-lg p-3 bg-slate-50" data-testid="late-add-panel">
        <div className="flex items-center gap-2 flex-wrap">
          <UserPlus className="w-4 h-4 text-slate-500" />
          <span className="text-xs font-medium text-slate-600">Found an address? Add it now — it'll be drafted, put in review, and saved to the client.</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-5 gap-2 mt-2">
          <select value={lateClientId} onChange={(e) => setLateClientId(e.target.value)}
            className="rounded-md border border-slate-300 px-2 py-1.5 text-sm" data-testid="late-add-client">
            <option value="">Which client…</option>
            {addable.map((g) => <option key={g.client_id} value={g.client_id}>{g.company_name}</option>)}
          </select>
          <Input placeholder="Name" value={manual.name} onChange={(e) => setManual({ ...manual, name: e.target.value })} data-testid="late-add-name" />
          <Input placeholder="Email" value={manual.email} onChange={(e) => setManual({ ...manual, email: e.target.value })} data-testid="late-add-email" />
          <Input placeholder="Title" value={manual.title} onChange={(e) => setManual({ ...manual, title: e.target.value })} data-testid="late-add-title" />
          <Button size="sm" disabled={busy || !lateClientId} onClick={() => addManual(lateClientId)} data-testid="late-add-save">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add & draft"}
          </Button>
        </div>
      </div>
    );
  };

  const draftAll = async () => {
    setBusy(true);
    try {
      const d = await api(`/campaigns/bespoke/${campaign.campaign_id}/draft-emails`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ regenerate: false }),
      });
      await refreshCampaign(campaign.campaign_id);
      toast.success(`Rex drafted ${d.drafted} email(s)${d.flagged ? ` · ${d.flagged} need house-style review` : ""}`);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  // Persist the current local edits for a recipient to the server, WITHOUT
  // triggering a refresh (avoids clobbering other in-progress local edits).
  const flushRecipient = async (rid) => {
    const ct = recipients.flatMap((g) => g.contacts).find((x) => x.recipient_id === rid);
    if (!ct) return;
    await api(`/campaigns/bespoke/${campaign.campaign_id}/recipients/${rid}`, {
      method: "PATCH", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        bespoke_subject: ct.bespoke_subject || "",
        bespoke_html: ct.bespoke_html || "",
        client_hook: ct.client_hook || "",
      }),
    });
  };

  const approveRecipient = async (rid) => {
    try {
      // Flush the latest local edits (subject/body/hook) BEFORE approving so the
      // approved snapshot never uses a stale subject if the field wasn't blurred.
      await flushRecipient(rid);
      await api(`/campaigns/bespoke/${campaign.campaign_id}/recipients/${rid}/approve`, { method: "POST" });
      await refreshCampaign(campaign.campaign_id);
    } catch (e) { toast.error(e.message); }
  };

  const uploadFile = async (file) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api(`/campaigns/bespoke/${campaign.campaign_id}/attachments`, { method: "POST", body: fd });
      await refreshCampaign(campaign.campaign_id);
      toast.success("File attached");
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const removeFile = async (fileId) => {
    await api(`/campaigns/bespoke/${campaign.campaign_id}/attachments/${fileId}`, { method: "DELETE" });
    await refreshCampaign(campaign.campaign_id);
  };

  const setTestModeServer = async (v) => {
    setTestMode(v);
    await api(`/campaigns/bespoke/${campaign.campaign_id}`, {
      method: "PATCH", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ test_mode: v }),
    });
  };

  const getCode = async () => {
    setBusy(true);
    try {
      const d = await api(`/campaigns/bespoke/${campaign.campaign_id}/approve`, { method: "POST" });
      if (d.test_mode && d.code) {
        setCode(d.code);
        toast.success(`Test mode: code ${d.code} (no email sent)`);
      } else if (d.code_delivery === "delayed") {
        toast.warning(d.warning || `Code couldn't be sent instantly to ${d.sender_email} — it was queued and may be delayed.`, { duration: 12000 });
      } else {
        toast.success(`Verification code sent to ${d.sender_email}`);
      }
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const launch = async () => {
    if (code.length !== 6) return toast.error("Enter the 6-digit code");
    setBusy(true);
    try {
      const d = await api(`/campaigns/bespoke/${campaign.campaign_id}/verify-send`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code }),
      });
      setSendResult(d);
      toast.success(d.simulated
        ? `Simulated ${d.enqueued} email(s) across ${d.waves} wave(s) — nothing sent`
        : `Launched ${d.enqueued} email(s) across ${d.waves} wave(s)`);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  // ── derived ──────────────────────────────────────────────────
  const allContacts = recipients.flatMap((g) => g.contacts);
  const included = allContacts.filter((c) => c.decision === "include");
  const includedDrafted = included.filter((c) => (c.bespoke_html || "").trim());
  const includedApproved = included.filter((c) => c.approval_status === "approved");
  const sendable = included.filter((c) => c.approval_status === "approved" && !isHardBlocked(c.verify_status));
  const allApproved = included.length > 0 && includedApproved.length === included.length;
  const selectedSender = senders.find((s) => s.sender_id === senderId);

  return (
    <div data-testid="bespoke-wizard" className="space-y-6">
      {/* Non-blocking verify acknowledgement — carries how risky the address is */}
      <AlertDialog open={!!ackDialog} onOpenChange={(o) => { if (!o) setAckDialog(null); }}>
        <AlertDialogContent data-testid="ack-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className={`w-5 h-5 ${ackDialog?.hard_blocked ? "text-red-600" : "text-amber-500"}`} />
              {ackDialog?.hard_blocked ? "This address looks undeliverable" : "Rex couldn't confirm this address"}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                {ackDialog?.hard_blocked ? (
                  <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-800" data-testid="ack-hardblocked">
                    Verification came back <strong>{ackDialog?.verify_status}</strong>. Sending to a dead address
                    hard-bounces and damages the sending domain's reputation for <em>every</em> future campaign.
                    Only add it if you know it's right.
                  </div>
                ) : (
                  <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800" data-testid="ack-softwarn">
                    Verification was inconclusive (<strong>{ackDialog?.verify_status}</strong>). It may still be fine —
                    you can add it and it'll go into review like the rest.
                  </div>
                )}
                <p className="text-xs text-slate-500">{ackDialog?.message}</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="ack-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={proceedAck}
              className={ackDialog?.hard_blocked ? "bg-red-600 hover:bg-red-700" : ""}
              data-testid="ack-confirm">
              Add anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* Stepper — click any completed step to jump back and edit it */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
        {STEPS.map((label, i) => {
          const reachable = i <= maxStep;
          return (
          <div key={label} className="flex items-center gap-2">
            <button
              type="button"
              disabled={!reachable}
              onClick={() => reachable && setStep(i)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
              i === step ? "bg-indigo-600 text-white"
              : i < step ? "bg-indigo-100 text-indigo-700" : "bg-slate-100 text-slate-400"} ${
              reachable && i !== step ? "hover:bg-indigo-200 cursor-pointer" : ""} ${
              !reachable ? "cursor-not-allowed" : ""}`}
              data-testid={`step-indicator-${i}`}>
              <span className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-xs">{i + 1}</span>
              {label}
            </button>
            {i < STEPS.length - 1 && <ChevronRight className="w-4 h-4 text-slate-300" />}
          </div>
          );
        })}
        </div>
        {campaign?.campaign_id && (
          <Button variant="outline" size="sm" data-testid="new-campaign-btn" onClick={resetWizard} disabled={busy}>
            <Plus className="w-3.5 h-3.5 mr-1" /> Start new campaign
          </Button>
        )}
      </div>

      {/* STEP 0 — Setup & Audience */}
      {step === 0 && (
        <Card data-testid="step-setup">
          <CardContent className="p-6 space-y-5">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Campaign name</Label>
                <Input data-testid="campaign-name-input" value={name} onChange={(e) => setName(e.target.value)}
                  placeholder="Q3 HR leaders outreach" className="mt-1" />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label>Send as (verified sender)</Label>
                  <button type="button" data-testid="add-sender-toggle" onClick={() => setShowAddSender((v) => !v)}
                    className="text-xs text-indigo-600 hover:text-indigo-700 flex items-center gap-1">
                    <UserPlus className="w-3.5 h-3.5" /> Add email account
                  </button>
                </div>
                <Select value={senderId} onValueChange={setSenderId}>
                  <SelectTrigger data-testid="sender-select" className="mt-1"><SelectValue placeholder="Select sender" /></SelectTrigger>
                  <SelectContent>
                    {senders.map((s) => (
                      <SelectItem key={s.sender_id} value={s.sender_id}>{s.display_name} · {s.email}</SelectItem>
                    ))}
                    {senders.length === 0 && <div className="px-2 py-3 text-xs text-slate-400">No senders yet — add one →</div>}
                  </SelectContent>
                </Select>
                {showAddSender && (
                  <div className="mt-2 p-3 bg-indigo-50 border border-indigo-100 rounded-lg space-y-2" data-testid="add-sender-form">
                    <div className="grid grid-cols-3 gap-2">
                      <Input placeholder="Name (e.g. Remi Dairo)" value={newSender.display_name}
                        onChange={(e) => setNewSender({ ...newSender, display_name: e.target.value })} data-testid="new-sender-name" />
                      <Input placeholder="Email (you@resiligent.com)" value={newSender.email}
                        onChange={(e) => setNewSender({ ...newSender, email: e.target.value })} data-testid="new-sender-email" />
                      <Input placeholder="Title (Managing Partner)" value={newSender.title}
                        onChange={(e) => setNewSender({ ...newSender, title: e.target.value })} data-testid="new-sender-title" />
                    </div>
                    <div>
                      <Label className="text-xs text-indigo-700">Email signature (optional)</Label>
                      <p className="text-[11px] text-indigo-400 mb-1">Paste it straight from Outlook/Gmail — formatting & logo are kept.</p>
                      <SignatureEditor value={newSender.signature_html}
                        onChange={(html) => setNewSender({ ...newSender, signature_html: html })}
                        testid="new-sender-signature" />
                    </div>
                    <div className="flex items-center justify-between">
                      <p className="text-[11px] text-indigo-500">A 6-digit code is emailed to this address at send time to authorise it. Use a real, monitored mailbox on an authenticated domain.</p>
                      <Button size="sm" onClick={addSender} disabled={busy} data-testid="save-sender-btn" className="bg-indigo-600 hover:bg-indigo-700">
                        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Add"}
                      </Button>
                    </div>
                  </div>
                )}
                {/* Edit signature for the selected existing sender */}
                {senderId && !showAddSender && (
                  <div className="mt-2">
                    <button type="button" data-testid="edit-signature-toggle"
                      onClick={() => { setSigDraft(selectedSender?.signature_html || ""); setEditSig((v) => !v); }}
                      className="text-xs text-slate-500 hover:text-indigo-600 flex items-center gap-1">
                      <Mail className="w-3.5 h-3.5" /> {selectedSender?.signature_html ? "Edit signature" : "Add signature"} for this account
                    </button>
                    {editSig && (
                      <div className="mt-2 p-3 bg-slate-50 border rounded-lg space-y-2" data-testid="signature-editor">
                        <p className="text-[11px] text-slate-500">Paste your signature directly from Outlook/Gmail — the logo, links and formatting are preserved. You can also edit text here.</p>
                        <SignatureEditor value={sigDraft} onChange={setSigDraft} testid="signature-input" />
                        <div className="flex items-center justify-end">
                          <Button size="sm" onClick={saveSignature} disabled={busy} data-testid="save-signature-btn">
                            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Save signature"}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div>
                <Label>Target type</Label>
                <Select value={targetType} onValueChange={setTargetType}>
                  <SelectTrigger data-testid="target-type-select" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TARGET_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Seniority</Label>
                <Select value={seniority} onValueChange={setSeniority}>
                  <SelectTrigger data-testid="seniority-select" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SENIORITY.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Send mode (Preserve default / Compose opt-in) */}
            <div data-testid="mode-selector" className="border rounded-lg p-3 bg-slate-50">
              <Label className="mb-2 block">Send mode</Label>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" data-testid="mode-preserve"
                  onClick={() => setSendMode("preserve")}
                  className={`text-left p-3 rounded-lg border transition-colors ${sendMode === "preserve" ? "border-indigo-500 bg-indigo-50 ring-1 ring-indigo-400" : "border-slate-200 bg-white hover:border-slate-300"}`}>
                  <div className="flex items-center gap-2 font-medium text-sm"><ShieldCheck className="w-4 h-4 text-emerald-600" /> Preserve <Badge variant="outline" className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]">default · safe</Badge></div>
                  <p className="text-xs text-slate-500 mt-1">Send your approved template exactly. Rex only fills [Name], [Company], [Signal] — it never rewrites the words.</p>
                </button>
                <button type="button" data-testid="mode-compose"
                  onClick={() => setSendMode("compose")}
                  className={`text-left p-3 rounded-lg border transition-colors ${sendMode === "compose" ? "border-indigo-500 bg-indigo-50 ring-1 ring-indigo-400" : "border-slate-200 bg-white hover:border-slate-300"}`}>
                  <div className="flex items-center gap-2 font-medium text-sm"><Sparkles className="w-4 h-4 text-violet-600" /> Compose <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-200 text-[10px]">opt-in</Badge></div>
                  <p className="text-xs text-slate-500 mt-1">Rex writes bespoke prose against a locked structure (mandatory authority line, approved libraries only).</p>
                </button>
              </div>
            </div>

            {sendMode === "preserve" ? (
              <>
                <div>
                  <Label>Subject line <span className="text-slate-400 text-xs">([Name] / [Company] allowed)</span></Label>
                  <Input data-testid="master-subject-input" value={masterSubject} onChange={(e) => setMasterSubject(e.target.value)}
                    placeholder="Proving leadership development works at [Company]" className="mt-1" />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label>Approved master template</Label>
                    <Button type="button" size="sm" variant="outline" data-testid="auto-tag-btn"
                      onClick={autoTagTemplate} disabled={busy || !masterTemplate.trim()}>
                      {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Wand2 className="w-3.5 h-3.5 mr-1" />}
                      Auto-tag slots
                    </Button>
                  </div>
                  <p className="text-xs text-slate-400 mb-1">
                    Paste your approved email, then click <strong>Auto-tag slots</strong> — Rex inserts <code>[Name]</code>, <code>[Company]</code> and <code>[Signal]</code> for you to check (or mark them yourself). It never changes your words. <strong>Don't sign your name at the end</strong>: finish with your closing line (e.g. "Kind regards,") and the sender's saved email signature is added automatically.
                  </p>
                  <Textarea data-testid="master-template-input" value={masterTemplate} onChange={(e) => setMasterTemplate(e.target.value)} rows={10}
                    placeholder={"Dear [Name],\n\nYou already know your high-potentials at [Company]... [Signal]\n\nI am the Managing Partner of RESILIGENT, and a former Chief Risk Officer...\n\nKind regards,"}
                    className="mt-1 font-mono text-xs" />
                </div>
              </>
            ) : (
              <>
                <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  Compose generates new prose. Rex keeps the mandatory Chief Risk Officer authority line and uses only approved tensions/proof points, but always review each draft before approving.
                </div>
                <div>
                  <Label>Campaign brief <span className="text-slate-400 text-xs">(Rex drafts a bespoke email per client from this)</span></Label>
                  <Textarea data-testid="brief-input" value={brief} onChange={(e) => setBrief(e.target.value)} rows={3}
                    placeholder="Who we are, why we're reaching out now, what we want…" className="mt-1" />
                </div>
                <div>
                  <Label>Call to action <span className="text-slate-400 text-xs">(optional)</span></Label>
                  <Input data-testid="cta-input" value={cta} onChange={(e) => setCta(e.target.value)}
                    placeholder="Propose a brief conversation." className="mt-1" />
                </div>
              </>
            )}

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="flex items-center gap-2"><Users className="w-4 h-4" /> Select clients ({selectedClients.length})</Label>
                <div className="flex gap-2">
                  <Select value={filterSector} onValueChange={setFilterSector}>
                    <SelectTrigger className="w-36 h-8 text-xs"><SelectValue placeholder="Sector" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All sectors</SelectItem>
                      {SECTORS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={filterTier} onValueChange={setFilterTier}>
                    <SelectTrigger className="w-28 h-8 text-xs"><SelectValue placeholder="Tier" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All tiers</SelectItem>
                      {TIERS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="border rounded-lg max-h-72 overflow-y-auto divide-y">
                {filteredClients.map((c) => (
                  <label key={c.client_id} data-testid={`client-row-${c.client_id}`}
                    className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50 cursor-pointer">
                    <Checkbox checked={selectedClients.includes(c.client_id)}
                      onCheckedChange={() => setSelectedClients((p) => p.includes(c.client_id) ? p.filter((x) => x !== c.client_id) : [...p, c.client_id])} />
                    <span className="font-medium text-sm flex-1">{c.company_name}</span>
                    <span className="text-xs text-slate-400">{c.sector}</span>
                    {c.tier && <Badge variant="outline" className="text-xs">{c.tier}</Badge>}
                  </label>
                ))}
                {filteredClients.length === 0 && <p className="text-center text-slate-400 text-sm py-6">No clients match</p>}
              </div>
            </div>

            {/* Pre-flight: one local check per selected client — see the gaps BEFORE you commit */}
            {preflight && selectedClients.length > 0 && (
              <div className="border rounded-lg p-4 bg-slate-50" data-testid="preflight-panel">
                <div className="flex items-center gap-2 mb-2">
                  <ShieldCheck className="w-4 h-4 text-indigo-600" />
                  <h4 className="text-sm font-semibold text-slate-800">Before you continue — {selectedClients.length} selected</h4>
                </div>
                <p className="text-xs text-slate-500 mb-3">
                  {preflight.counts.ready || 0} ready · {(preflight.counts.contacts_no_match || 0)} no match for this target ·
                  {" "}{(preflight.counts.domain_searchable || 0)} searchable · {(preflight.counts.no_contact_no_domain || 0)} no contact/domain ·
                  {" "}{(preflight.counts.restricted || 0)} restricted
                </p>
                <div className="space-y-1.5">
                  {preflight.clients.map((p) => {
                    const tone = {
                      ready: "text-emerald-700 bg-emerald-50",
                      contacts_no_match: "text-amber-700 bg-amber-50",
                      domain_searchable: "text-blue-700 bg-blue-50",
                      no_contact_no_domain: "text-slate-600 bg-slate-100",
                      restricted: "text-red-700 bg-red-50",
                    }[p.status] || "text-slate-600 bg-slate-100";
                    return (
                      <div key={p.client_id} className="flex items-center gap-2 text-xs" data-testid={`preflight-${p.client_id}`}>
                        <span className="font-medium text-slate-700 w-48 truncate">{p.company_name}</span>
                        <span className={`rounded px-2 py-0.5 ${tone}`}>{p.label}</span>
                        {p.status === "domain_searchable" && (
                          <Button size="sm" variant="outline" className="h-6 text-xs ml-auto"
                            disabled={discoveringFor === p.client_id}
                            onClick={() => findContacts(p.client_id)} data-testid={`find-contacts-${p.client_id}`}>
                            {discoveringFor === p.client_id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Search className="w-3 h-3 mr-1" />}
                            Find contacts
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
                {/* Discovered candidates awaiting confirmation → written back to the client */}
                {Object.entries(candidates).map(([cid, list]) => (list && list.length > 0) && (
                  <div key={cid} className="mt-3 border-t pt-2" data-testid={`candidates-${cid}`}>
                    <p className="text-xs font-medium text-slate-600 mb-1">Proposed contacts (confirm to save to the client record):</p>
                    {list.map((cand) => (
                      <div key={cand.email} className="flex items-center gap-2 text-xs py-0.5">
                        <span className="font-medium w-40 truncate">{cand.name || "—"}</span>
                        <span className="text-slate-500 flex-1 truncate">{cand.email}</span>
                        <span className="text-slate-400 w-28 truncate">{cand.title}</span>
                        <Button size="sm" className="h-6 text-xs bg-emerald-600 hover:bg-emerald-700"
                          disabled={busy} onClick={() => confirmCandidate(cid, cand)} data-testid="confirm-candidate">
                          Confirm
                        </Button>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}

            <div className="flex justify-end gap-2">
              {campaign?.campaign_id ? (
                <>
                  <Button data-testid="re-resolve-btn" onClick={reResolve} disabled={busy} variant="outline">
                    {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                    Re-find contacts
                  </Button>
                  <Button data-testid="save-setup-btn" onClick={saveSetup} disabled={busy}
                    className="bg-indigo-600 hover:bg-indigo-700">
                    {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    Save &amp; continue <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </>
              ) : (
                <Button data-testid="create-resolve-btn" onClick={createAndResolve} disabled={busy}
                  className="bg-indigo-600 hover:bg-indigo-700">
                  {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                  Find contacts <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* STEP 1 — Contacts */}
      {step === 1 && campaign && (
        <Card data-testid="step-contacts">
          <CardContent className="p-6 space-y-5">
            <p className="text-sm text-slate-500">Review who Rex found. Toggle who it goes to, add people manually, then continue. Only verified addresses are included by default.</p>
            {recipients.map((g) => (
              <div key={g.client_id} className={`border rounded-lg p-4 ${g.restricted ? "border-red-200 bg-red-50" : ""}`} data-testid={`contact-group-${g.client_id}`}>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-slate-800">{g.company_name}</h4>
                  {g.restricted ? (
                    <Badge className="bg-red-600 text-white text-xs" data-testid={`restricted-${g.client_id}`}>
                      <Ban className="w-3.5 h-3.5 mr-1" /> Restricted — cannot be contacted
                    </Badge>
                  ) : (
                    <Button size="sm" variant="outline" data-testid={`add-manual-${g.client_id}`}
                      onClick={() => setManualFor(manualFor === g.client_id ? null : g.client_id)}>
                      <Plus className="w-3.5 h-3.5 mr-1" /> Add contact
                    </Button>
                  )}
                </div>
                {g.restricted ? (
                  <p className="text-xs text-red-700" data-testid={`restricted-note-${g.client_id}`}>
                    This organisation is on the do-not-contact list. Rex will not add it to the campaign and offers no way to do so here.
                  </p>
                ) : (
                <>
                {manualFor === g.client_id && (
                  <div className="grid grid-cols-4 gap-2 mb-3 bg-slate-50 p-3 rounded-lg">
                    <Input placeholder="Name" value={manual.name} onChange={(e) => setManual({ ...manual, name: e.target.value })} data-testid="manual-name" />
                    <Input placeholder="Email" value={manual.email} onChange={(e) => setManual({ ...manual, email: e.target.value })} data-testid="manual-email" />
                    <Input placeholder="Title" value={manual.title} onChange={(e) => setManual({ ...manual, title: e.target.value })} data-testid="manual-title" />
                    <Button size="sm" onClick={() => addManual(g.client_id)} disabled={busy} data-testid="manual-save">Add</Button>
                  </div>
                )}
                <div className="space-y-2">
                  {g.contacts.map((ct) => (
                    <div key={ct.recipient_id} className="flex items-center gap-3 text-sm" data-testid={`contact-${ct.recipient_id}`}>
                      <Checkbox checked={ct.decision === "include"} disabled={isHardBlocked(ct.verify_status)}
                        data-testid={`include-${ct.recipient_id}`}
                        onCheckedChange={(v) => {
                          const decision = v ? "include" : "exclude";
                          // Optimistic: reflect the choice instantly, then persist.
                          setRecipients((prev) => prev.map((g) => ({ ...g, contacts: g.contacts.map((x) => x.recipient_id === ct.recipient_id ? { ...x, decision } : x) })));
                          patchRecipient(ct.recipient_id, { decision });
                        }} />
                      <span className="font-medium w-40 truncate">{ct.name || "—"}</span>
                      <span className="text-slate-500 flex-1 truncate">{ct.email}</span>
                      <span className="text-slate-400 text-xs w-32 truncate">{ct.title}</span>
                      <Badge variant="outline" className="text-xs capitalize">{ct.source}</Badge>
                      {verifyBadge(ct.verify_status)}
                    </div>
                  ))}
                  {g.contacts.length === 0 && <p className="text-xs text-slate-400">No contacts found — add one manually.</p>}
                </div>
                </>
                )}
              </div>
            ))}
            <div className="flex justify-between">
              <Button variant="ghost" onClick={() => setStep(0)}><ChevronLeft className="w-4 h-4 mr-1" /> Back</Button>
              <Button data-testid="to-compose-btn" disabled={included.length === 0}
                onClick={() => setStep(2)} className="bg-indigo-600 hover:bg-indigo-700">
                Compose ({included.length} included) <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* STEP 2 — Compose */}
      {step === 2 && campaign && (
        <Card data-testid="step-compose">
          <CardContent className="p-6 space-y-5">
            <div className="flex items-center justify-between">
              <p className="text-sm text-slate-500">Rex drafts a bespoke email per client. Review, edit, then <strong>approve each one</strong>. Approval is mandatory before sending.</p>
              <Button data-testid="rex-draft-btn" onClick={draftAll} disabled={busy} className="bg-violet-600 hover:bg-violet-700">
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                Rex draft per client
              </Button>
            </div>
            <div className="text-xs text-slate-500">{includedApproved.length}/{included.length} approved · {includedDrafted.length} drafted</div>
            {renderLateAdd()}
            {included.map((ct) => (
              <div key={ct.recipient_id} className="border rounded-lg p-4 space-y-2" data-testid={`draft-${ct.recipient_id}`}>
                <div className="flex items-center justify-between">
                  <div className="text-sm">
                    <span className="font-semibold">{ct.name || ct.email}</span>
                    <span className="text-slate-400 ml-2">{ct.company_name}</span>
                  </div>
                  {ct.approval_status === "approved"
                    ? <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200"><CheckCircle2 className="w-3 h-3 mr-1" /> Approved</Badge>
                    : <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">Needs review</Badge>}
                </div>
                <Input placeholder="Hook (why them, why now)" value={ct.client_hook || ""}
                  data-testid={`hook-${ct.recipient_id}`}
                  onChange={(e) => setRecipients((prev) => prev.map((g) => ({ ...g, contacts: g.contacts.map((x) => x.recipient_id === ct.recipient_id ? { ...x, client_hook: e.target.value } : x) })))}
                  onBlur={(e) => patchRecipient(ct.recipient_id, { client_hook: e.target.value })}
                  className="text-xs" />
                {(ct.bespoke_html || "").trim() ? (
                  <>
                    {/* Compose-mode mandatory authority line check */}
                    {ct.authority_ok === false && (
                      <div data-testid={`authority-warning-${ct.recipient_id}`}
                        className="flex items-start gap-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-2">
                        <ShieldAlert className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <span><strong>Mandatory authority line missing.</strong> The Chief Risk Officer line must appear before this can go out. Edit the draft to add it.</span>
                      </div>
                    )}
                    {/* House-style violations (advisory — operator reviews before approving) */}
                    {Array.isArray(ct.house_style_flags) && ct.house_style_flags.length > 0 && (
                      <div data-testid={`house-style-flags-${ct.recipient_id}`}
                        className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2">
                        <div className="flex items-center gap-1.5 font-medium mb-1">
                          <AlertTriangle className="w-3.5 h-3.5" /> House-style check — {ct.house_style_flags.length} issue(s)
                        </div>
                        <ul className="list-disc list-inside space-y-0.5 text-amber-700">
                          {ct.house_style_flags.map((f, i) => <li key={i}>{f}</li>)}
                        </ul>
                        {campaign?.send_mode === "compose" && (
                          <Button size="sm" variant="outline" disabled={busy}
                            onClick={() => autoFixStyle(ct.recipient_id)}
                            data-testid={`auto-fix-style-${ct.recipient_id}`}
                            className="mt-2 h-7 text-xs border-amber-300 text-amber-800 hover:bg-amber-100">
                            <Wand2 className="w-3.5 h-3.5 mr-1" /> Auto-fix house-style
                          </Button>
                        )}
                      </div>
                    )}
                    {Array.isArray(ct.house_style_flags) && ct.house_style_flags.length === 0 && ct.authority_ok !== false && (
                      <div className="flex items-center gap-1.5 text-xs text-emerald-700" data-testid={`house-style-clean-${ct.recipient_id}`}>
                        <CheckCircle2 className="w-3.5 h-3.5" /> House-style check passed
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <Input value={ct.bespoke_subject || ""} placeholder="Subject" data-testid={`subject-${ct.recipient_id}`}
                        onChange={(e) => setRecipients((prev) => prev.map((g) => ({ ...g, contacts: g.contacts.map((x) => x.recipient_id === ct.recipient_id ? { ...x, bespoke_subject: e.target.value } : x) })))}
                        onBlur={(e) => patchRecipient(ct.recipient_id, { bespoke_subject: e.target.value })} className="flex-1 mr-2" />
                      <Button size="sm" variant="outline" data-testid={`preview-toggle-${ct.recipient_id}`}
                        onClick={() => togglePreview(ct.recipient_id)}>
                        {previews[ct.recipient_id] ? <><Code2 className="w-3.5 h-3.5 mr-1" /> Edit</> : <><Eye className="w-3.5 h-3.5 mr-1" /> Preview</>}
                      </Button>
                    </div>
                    {previews[ct.recipient_id] ? (
                      <div data-testid={`preview-${ct.recipient_id}`}
                        className="border rounded-md bg-white p-3 text-sm prose prose-sm max-w-none min-h-[120px]"
                        dangerouslySetInnerHTML={{ __html: ct.bespoke_html || "" }} />
                    ) : (
                      <Textarea rows={5} value={ct.bespoke_html || ""} data-testid={`body-${ct.recipient_id}`}
                        onChange={(e) => setRecipients((prev) => prev.map((g) => ({ ...g, contacts: g.contacts.map((x) => x.recipient_id === ct.recipient_id ? { ...x, bespoke_html: e.target.value } : x) })))}
                        onBlur={(e) => patchRecipient(ct.recipient_id, { bespoke_html: e.target.value })} className="text-xs font-mono" />
                    )}
                    <div className="flex justify-end">
                      <Button size="sm" disabled={ct.approval_status === "approved"} data-testid={`approve-${ct.recipient_id}`}
                        onClick={() => approveRecipient(ct.recipient_id)} className="bg-emerald-600 hover:bg-emerald-700">
                        <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> {ct.approval_status === "approved" ? "Approved" : "Approve"}
                      </Button>
                    </div>
                  </>
                ) : <p className="text-xs text-slate-400 italic">Not drafted yet — click "Rex draft per client".</p>}
              </div>
            ))}
            <div className="flex justify-between">
              <Button variant="ghost" onClick={() => setStep(1)}><ChevronLeft className="w-4 h-4 mr-1" /> Back</Button>
              <Button data-testid="to-files-btn" disabled={!allApproved} onClick={() => setStep(3)} className="bg-indigo-600 hover:bg-indigo-700">
                Files <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
            {!allApproved && included.length > 0 && (
              <p className="text-xs text-amber-600 text-right">Approve every included recipient to continue.</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* STEP 3 — Files */}
      {step === 3 && campaign && (
        <Card data-testid="step-files">
          <CardContent className="p-6 space-y-4">
            <p className="text-sm text-slate-500">Attach files (brochure etc.) — under 15MB total. These attach to every email.</p>
            <label className="flex flex-col items-center justify-center border-2 border-dashed rounded-lg p-8 cursor-pointer hover:bg-slate-50" data-testid="file-dropzone">
              <FileUp className="w-8 h-8 text-slate-400 mb-2" />
              <span className="text-sm text-slate-500">Click to upload (pdf, docx, pptx, xlsx, png, jpg)</span>
              <input type="file" className="hidden" data-testid="file-input"
                accept=".pdf,.docx,.pptx,.xlsx,.png,.jpg,.jpeg"
                onChange={(e) => { if (e.target.files[0]) uploadFile(e.target.files[0]); e.target.value = ""; }} />
            </label>
            <div className="space-y-2">
              {(campaign.attachments || []).map((a) => (
                <div key={a.file_id} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2" data-testid={`attachment-${a.file_id}`}>
                  <span className="text-sm">{a.filename} <span className="text-slate-400 text-xs">({Math.round(a.size / 1024)} KB)</span></span>
                  <Button size="sm" variant="ghost" onClick={() => removeFile(a.file_id)} data-testid={`remove-att-${a.file_id}`}>
                    <Trash2 className="w-4 h-4 text-rose-500" />
                  </Button>
                </div>
              ))}
              {(campaign.attachments || []).length === 0 && <p className="text-xs text-slate-400">No files attached (optional).</p>}
            </div>
            <div className="flex justify-between">
              <Button variant="ghost" onClick={() => setStep(2)}><ChevronLeft className="w-4 h-4 mr-1" /> Back</Button>
              <Button data-testid="to-review-btn" onClick={() => setStep(4)} className="bg-indigo-600 hover:bg-indigo-700">
                Review & Send <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* STEP 4 — Review & Send */}
      {step === 4 && campaign && (
        <Card data-testid="step-review">
          <CardContent className="p-6 space-y-5">
            {renderLateAdd()}
            <P1GreetingPanel
              campaign={campaign}
              recipients={recipients}
              api={api}
              onRefresh={() => refreshCampaign(campaign.campaign_id)}
            />
            <div className="grid md:grid-cols-2 gap-4 text-sm">
              <div className="bg-slate-50 rounded-lg p-4 space-y-1">
                <p><span className="text-slate-400">Sender:</span> <strong>{selectedSender?.display_name}</strong> · {selectedSender?.email}</p>
                <p><span className="text-slate-400">Reply-To:</span> {campaign.reply_to_email}</p>
                <p><span className="text-slate-400">Sendable recipients:</span> <strong>{sendable.length}</strong> across {recipients.length} client(s)</p>
                <p><span className="text-slate-400">Attachments:</span> {(campaign.attachments || []).length}</p>
                <p><span className="text-slate-400">Signature:</span> {selectedSender?.signature_html ? <span className="text-emerald-600">appended ✓</span> : <span className="text-amber-600">none set</span>}</p>
              </div>
              <div className="rounded-lg p-4 border border-amber-200 bg-amber-50 space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2 text-amber-800"><ShieldCheck className="w-4 h-4" /> Test mode (no emails sent)</Label>
                  <Switch checked={testMode} onCheckedChange={setTestModeServer} data-testid="test-mode-switch" />
                </div>
                <p className="text-xs text-amber-700">
                  {testMode ? "Simulates the send. The verification code is shown on-screen and nothing is emailed."
                    : "LIVE: real emails will be sent in waves of 5 clients. The code is emailed to the sender."}
                </p>
              </div>
            </div>

            <div className="border rounded-lg p-4 space-y-3">
              <div className="flex items-center gap-3">
                <Button data-testid="get-code-btn" onClick={getCode} disabled={busy || sendable.length === 0} variant="outline">
                  {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
                  {testMode ? "Get test code" : "Send verification code"}
                </Button>
                <Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="6-digit code" className="w-40" data-testid="code-input" />
                <Button data-testid="launch-btn" onClick={launch} disabled={busy || code.length !== 6}
                  className={testMode ? "bg-slate-700 hover:bg-slate-800" : "bg-emerald-600 hover:bg-emerald-700"}>
                  {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  {testMode ? "Simulate send" : "Launch send"}
                </Button>
              </div>
              {sendResult && (
                <div className="text-sm rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-emerald-800" data-testid="send-result">
                  {sendResult.simulated ? "✓ Simulated" : "✓ Launched"} <strong>{sendResult.enqueued}</strong> email(s) across <strong>{sendResult.waves}</strong> wave(s)
                  {sendResult.simulated && " — nothing was actually sent."}
                </div>
              )}
            </div>

            <SchedulePanel
              campaign={campaign}
              code={code}
              sendableCount={sendable.length}
              defaultDailyLimit={selectedSender?.daily_limit}
              onConsumedCode={() => setCode("")}
            />

            <SequencePanel campaign={campaign} />
            <div className="flex justify-start">
              <Button variant="ghost" onClick={() => setStep(3)}><ChevronLeft className="w-4 h-4 mr-1" /> Back</Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
